import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  HStack,
  Skeleton,
  Spacer,
  Stack,
} from '@chakra-ui/react';
import {
  ColumnDef,
  SortingState,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
} from '@tanstack/react-table';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';

import { Bank } from '@finley-technologies/finley-types';

import {
  TaskFragmentGQLType,
  TaskStatusGQLType,
  useGetTasksForOrganizationQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import {
  taskStatuses,
  getActionRequiredFromCategorySpecificProperties,
  getDueDateFromCategorySpecificProperties,
  getLoanIdFromCategorySpecificProperties,
  TaskStatus,
} from './common';
import { DownloadTaskCenterButton } from './DownloadTaskCenterButton';
import { TaskDetailDrawer } from './TaskCenterDrawer';
import { TaskCenterFiltersDrawer } from './TaskCenterFilter';
import { Counter } from 'packages/common-frontend/components/Counter';
import {
  CsvXlsxDownloadSelect,
  getTanstackVisibleDataForDownload,
} from 'packages/common-frontend/components/CsvXlsxDownloadSelect';
import { Typography } from 'packages/common-frontend/components/Typography';
import { TableBETA } from 'packages/common-frontend/tables/TableBETA';
import { ColorScheme } from 'packages/common-frontend/theme';
import { remToPx } from 'packages/common-frontend/theme/utils';
import {
  formatCurrencyAmount,
  truncateUuid,
} from 'packages/common-frontend/utils';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';

const statusColorScheme: { [key in TaskStatusGQLType]: ColorScheme } = {
  overdue: 'red',
  dueSoon: 'orange',
  completed: 'green',
  archived: 'gray',
};

const TaskTableSection = ({
  status,
  tasks,
}: {
  status: TaskStatusGQLType;
  tasks: TaskFragmentGQLType[];
}) => {
  const columns = useMemo(() => {
    // these columns should also ALL exist in the `exportToXlsx` logic `DownloadTaskCenterButton.tsx`
    const tableColumns: ColumnDef<TaskFragmentGQLType, any>[] = [
      {
        id: 'creditFacilityName',
        accessorFn: (cell) =>
          _.startCase(cell.domainProperties.creditFacility.name),
        enableSorting: true,
        size: remToPx(13),
        header: 'Facility',
      },
      {
        id: 'loanId',
        accessorFn: (cell) => {
          const loanId = getLoanIdFromCategorySpecificProperties(
            cell.domainProperties.categorySpecificProperties,
          );

          return loanId ? truncateUuid(loanId) : '-';
        },
        enableSorting: true,
        size: remToPx(10),
        header: 'Loan',
      },
      {
        id: 'category',
        accessorFn: (cell) =>
          _.startCase(
            cell.domainProperties.categorySpecificProperties.category,
          ),
        header: 'Category',
        enableSorting: true,
        size: remToPx(14),
      },
      {
        id: 'actionRequired',
        accessorFn: (cell) =>
          getActionRequiredFromCategorySpecificProperties(
            cell.domainProperties.categorySpecificProperties,
          ),
        header: 'Action Required',
        enableSorting: true,
        size: remToPx(10),
      },
      {
        id: 'amount',
        accessorFn: (cell) =>
          formatCurrencyAmount(cell.domainProperties.amount, Bank.Currency.USD),
        header: 'Amount',
        enableSorting: true,
        size: remToPx(9),
      },
      {
        id: 'partyName',
        accessorFn: (cell) => _.startCase(cell.domainProperties.party.name),
        header: 'Party Name',
        enableSorting: true,
        size: remToPx(16),
      },
      {
        id: 'dueDate',
        accessorFn: (cell) =>
          getDueDateFromCategorySpecificProperties(
            cell.domainProperties.categorySpecificProperties,
          ) ?? '-',
        header: 'Due Date',
        enableSorting: true,
        size: remToPx(9),
      },
    ];

    return tableColumns;
  }, []);

  const [sorting, setSorting] = React.useState<SortingState>([]);

  const table = useReactTable({
    data: tasks,
    columns,
    enableMultiSort: true,
    state: { sorting },
    initialState: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const csvDownload = getTanstackVisibleDataForDownload({
    tableInstance: table,
  });

  const [selectedTask, setSelectedTask] = useState<TaskFragmentGQLType>();

  return (
    <Stack key={status} gap="1rem">
      <AccordionItem
        width="full"
        style={{ borderTopWidth: '0px', borderBottomWidth: '0px' }}
      >
        <HStack padding="0.5em">
          <AccordionButton width="full">
            <HStack gap="1rem" width="full">
              <Typography variant="section-title" trim>
                {_.startCase(status)}
              </Typography>
              <Counter
                count={tasks.length}
                colorScheme={statusColorScheme[status]}
              />

              <Spacer />
            </HStack>
          </AccordionButton>

          <Box style={{ alignSelf: 'center' }}>
            <CsvXlsxDownloadSelect
              dataRows={csvDownload.dataRows}
              headers={csvDownload.headers}
              filenamePrefix={`task-center-${status}`}
            />
          </Box>
        </HStack>
        <AccordionPanel>
          <TableBETA
            table={table}
            onRowClick={(row) => {
              setSelectedTask(row);
            }}
          />
        </AccordionPanel>
      </AccordionItem>

      {selectedTask && (
        <TaskDetailDrawer
          selectedTask={selectedTask}
          onClose={() => {
            setSelectedTask(undefined);
          }}
        />
      )}
    </Stack>
  );
};

// ******************************************************
// *                    Task Center                     *
// ******************************************************
export const TaskCenter = () => {
  const {
    user: { organizationId },
  } = useUserAuthOrThrow();

  const [isRefetching, setIsRefetching] = useState(false);

  const taskForOrgQuery = useGetTasksForOrganizationQuery({
    variables: {
      organizationId,
    },
  });

  const tasksByStatus = useMemo(() => {
    if (taskForOrgQuery.data) {
      return _.groupBy(
        taskForOrgQuery.data.getTasksForOrganization.tasks,
        (t) => t.domainProperties.status,
      );
    }
    return null;
  }, [taskForOrgQuery.data]);

  const filterState = useMemo(() => {
    if (taskForOrgQuery.data) {
      return taskForOrgQuery.data?.getTasksForOrganization.filterState;
    }
    return null;
  }, [taskForOrgQuery.data]);

  const defaultIndex = useMemo(() => {
    if (tasksByStatus) {
      const statuses: TaskStatus[] = [];

      taskStatuses.forEach((status) => {
        const tasksForStatus = tasksByStatus[status];

        if (tasksForStatus) {
          statuses.push(status);
        }
      });

      const accordionOpenIndicies: number[] = [];
      statuses.forEach((status, index) => {
        if (status === 'overdue' || status === 'dueSoon') {
          accordionOpenIndicies.push(index);
        }
      });

      return accordionOpenIndicies;
    }

    return null;
  }, [tasksByStatus]);

  if (taskForOrgQuery.error) {
    throw new Error(`Failed to get Tasks. Please contact Finley support.`);
  }

  if (!tasksByStatus || !filterState || !defaultIndex) {
    return <Skeleton h="400px" />;
  }

  return (
    <Stack>
      <HStack gap="1em" mb="1.5rem">
        <Typography variant="page-title" trim>
          Task
        </Typography>
        <Spacer />
        {!isRefetching && (
          <TaskCenterFiltersDrawer
            key={JSON.stringify(filterState)}
            onFilterChange={async (newFilters) => {
              setIsRefetching(true);
              await taskForOrgQuery.refetch({
                organizationId,
                filters: newFilters,
              });
              setIsRefetching(false);
            }}
            initialFilter={{
              creditFacilityIds: filterState.currentFilters.creditFacilityIds,
              categories: filterState.currentFilters.categories,
              loanIds: filterState.currentFilters.loanIds ?? undefined,
              dateRange: filterState.currentFilters.dateRange ?? undefined,
            }}
            allLoanIds={filterState.allLoanIds}
          />
        )}
        {!isRefetching && Object.keys(tasksByStatus).length > 0 && (
          <DownloadTaskCenterButton tasksByStatus={tasksByStatus} />
        )}
      </HStack>

      {isRefetching ? (
        <Skeleton height="400px" />
      ) : (
        <Stack gap="2rem">
          {Object.keys(tasksByStatus).length === 0 ? (
            <Typography>No Tasks</Typography>
          ) : (
            <Accordion defaultIndex={defaultIndex} allowMultiple allowToggle>
              {taskStatuses.map((status) => {
                const tasksForStatus = tasksByStatus[status];

                if (!tasksForStatus) {
                  return <></>;
                }

                return (
                  <TaskTableSection
                    key={status}
                    status={status}
                    tasks={tasksForStatus}
                  />
                );
              })}
            </Accordion>
          )}
        </Stack>
      )}
    </Stack>
  );
};
